import { useEffect, useMemo, useState } from 'react';
import { Button, Group, Modal, NumberInput, Select, Stack, TextInput } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus } from 'lucide-react';
import { api } from '@/api/client';
import { useLocations, useProducts, useProductTypes } from '@/api/products';
import type { Product } from '@/api/types';
import { LockerBadges } from '@/components/LockerBadges';
import { Page, QueryState } from '@/components/Page';
import { ResponsiveTable, type Column } from '@/components/ResponsiveTable';
import { formatRub } from '@/lib/format';
import { notifyError, notifySuccess } from '@/lib/notify';

function ProductModal({ product, opened, onClose }: { product: Product | null; opened: boolean; onClose: () => void }) {
    const queryClient = useQueryClient();
    const types = useProductTypes();
    const fullScreen = useMediaQuery('(max-width: 48em)');
    const [name, setName] = useState('');
    const [price, setPrice] = useState<number | string>('');
    const [typeId, setTypeId] = useState<string | null>(null);

    useEffect(() => {
        if (!opened) return;
        setName(product?.name ?? '');
        setPrice(product ? Number(product.price_rub) : '');
        setTypeId(product?.type_id ? String(product.type_id) : null);
    }, [opened, product]);

    const save = useMutation({
        mutationFn: () => {
            const body = { name: name.trim(), price_rub: price, type_id: typeId };
            return product ? api.put(`/api/admin/products/${product.id}`, body) : api.post('/api/admin/products', body);
        },
        onSuccess: () => {
            notifySuccess('Продукт сохранён');
            queryClient.invalidateQueries({ queryKey: ['products'] });
            onClose();
        },
        onError: notifyError,
    });

    return (
        <Modal opened={opened} onClose={onClose} title={product ? 'Продукт' : 'Новый продукт'} fullScreen={fullScreen}>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    save.mutate();
                }}
            >
                <Stack>
                    <TextInput label="Название" required value={name} onChange={(e) => setName(e.currentTarget.value)} />
                    <Select
                        label="Тип"
                        required
                        placeholder="Выберите тип"
                        data={(types.data ?? []).map((t) => ({ value: String(t.id), label: t.name }))}
                        value={typeId}
                        onChange={setTypeId}
                    />
                    <NumberInput label="Цена, ₽" required min={0} decimalScale={2} value={price} onChange={setPrice} />
                    <Group justify="flex-end" mt="sm">
                        <Button variant="default" onClick={onClose}>
                            Отмена
                        </Button>
                        <Button type="submit" loading={save.isPending} disabled={!name.trim() || !typeId || price === ''}>
                            Сохранить
                        </Button>
                    </Group>
                </Stack>
            </form>
        </Modal>
    );
}

export function ProductsScreen() {
    const products = useProducts();
    const locations = useLocations();
    const [editing, setEditing] = useState<Product | null>(null);
    const [opened, setOpened] = useState(false);

    // Имя и онлайн постамата берём из локаций: в products.lockers только locker_id + метка.
    const lockerIndex = useMemo(() => {
        const map = new Map<string, { name: string; online?: boolean }>();
        for (const loc of locations.data ?? []) {
            for (const l of loc.lockers ?? []) map.set(l.locker_id, { name: l.display_name || l.locker_id, online: l.is_online });
        }
        return map;
    }, [locations.data]);

    const open = (p: Product | null) => {
        setEditing(p);
        setOpened(true);
    };

    const columns: Column<Product>[] = [
        { key: 'name', title: 'Название', mobile: 'title', render: (p) => p.name },
        { key: 'type', title: 'Тип', mobile: 'subtitle', render: (p) => p.type_name || '—' },
        { key: 'price', title: 'Цена', render: (p) => formatRub(p.price_rub) },
        {
            key: 'lockers',
            title: 'Постаматы',
            render: (p) => (
                <LockerBadges
                    items={(p.lockers ?? []).map((l) => {
                        const info = lockerIndex.get(l.locker_id);
                        return { locker_id: l.locker_id, name: info?.name || l.label || l.locker_id, online: info?.online };
                    })}
                />
            ),
        },
        { key: 'id', title: 'ID', mobile: 'hidden', render: (p) => p.id },
    ];

    return (
        <Page
            title="Продукты"
            actions={
                <Button leftSection={<Plus size={16} />} onClick={() => open(null)}>
                    Добавить
                </Button>
            }
        >
            <QueryState isLoading={products.isLoading} error={products.error} onRetry={products.refetch}>
                <ResponsiveTable
                    data={products.data ?? []}
                    columns={columns}
                    rowKey={(p) => p.id}
                    onRowClick={open}
                    empty="Продуктов нет"
                    actions={(p) => (
                        <Button size="compact-sm" variant="light" leftSection={<Pencil size={14} />} onClick={() => open(p)}>
                            Изменить
                        </Button>
                    )}
                />
            </QueryState>
            <ProductModal product={editing} opened={opened} onClose={() => setOpened(false)} />
        </Page>
    );
}
